/**
 * secretCrypto.js —— 前端敏感密钥对称加密工具
 *
 * 设计说明：
 * 1. 采用 AES-128-CTR 对称加密，每次加密都会生成一份【随机 nonce】（随机数），
 *    因此同一个密钥每次生成的密文都不同，源码与打包产物中不会出现任何明文密钥。
 * 2. 加密结果统一为字符串令牌：ENC$<base64(nonce)>$<base64(密文)>
 * 3. 对 JSON.stringify 做了一次安全增强：任何带有 ENC$ 令牌的字符串在被序列化
 *    （即真正发送给 ComfyUI / OSS 等后端）的瞬间自动还原为明文，
 *    因此业务功能完全不受影响。
 * 4. 运行时解密密钥由多个掩码片段在内存中动态还原，不以完整字符串形式存在。
 */

import axios from 'axios'

// ---------------------------------------------------------------------------
// AES-128 基础实现（CTR 模式只需正向分组加密）
// ---------------------------------------------------------------------------
const SBOX = [
  99, 124, 119, 123, 242, 107, 111, 197, 48, 1, 103, 43, 254, 215, 171, 118,
  202, 130, 201, 125, 250, 89, 71, 240, 173, 212, 162, 175, 156, 164, 114, 192,
  183, 253, 147, 38, 54, 63, 247, 204, 52, 165, 229, 241, 113, 216, 49, 21,
  4, 199, 35, 195, 24, 150, 5, 154, 7, 18, 128, 226, 235, 39, 178, 117,
  9, 131, 44, 26, 27, 110, 90, 160, 82, 59, 214, 179, 41, 227, 47, 132,
  83, 209, 0, 237, 32, 252, 177, 91, 106, 203, 190, 57, 74, 76, 88, 207,
  208, 239, 170, 251, 67, 77, 51, 133, 69, 249, 2, 127, 80, 60, 159, 168,
  81, 163, 64, 143, 146, 157, 56, 245, 188, 182, 218, 33, 16, 255, 243, 210,
  205, 12, 19, 236, 95, 151, 68, 23, 196, 167, 126, 61, 100, 93, 25, 115,
  96, 129, 79, 220, 34, 42, 144, 136, 70, 238, 184, 20, 222, 94, 11, 219,
  224, 50, 58, 10, 73, 6, 36, 92, 194, 211, 172, 98, 145, 149, 228, 121,
  231, 200, 55, 109, 141, 213, 78, 169, 108, 86, 244, 234, 101, 122, 174, 8,
  186, 120, 37, 46, 28, 166, 180, 198, 232, 221, 116, 31, 75, 189, 139, 138,
  112, 62, 181, 102, 72, 3, 246, 14, 97, 53, 87, 185, 134, 193, 29, 158,
  225, 248, 152, 17, 105, 217, 142, 148, 155, 30, 135, 233, 206, 85, 40, 223,
  140, 161, 137, 13, 191, 230, 66, 104, 65, 153, 45, 15, 176, 84, 187, 22
]

function xtime (a) {
  return ((a << 1) ^ (((a >> 7) & 1) * 0x1b)) & 0xff
}

function gmul (a, b) {
  let p = 0
  let i
  let aa = a
  let bb = b
  for (i = 0; i < 8; i++) {
    if (bb & 1) p ^= aa
    bb >>= 1
    aa = xtime(aa)
  }
  return p & 0xff
}

// AES-128 密钥扩展
function expandKey (key) {
  const RCON = [1, 2, 4, 8, 16, 32, 64, 128, 27, 54]
  const w = new Array(176)
  let i
  for (i = 0; i < 16; i++) w[i] = key[i]
  for (i = 16; i < 176; i += 4) {
    let t0 = w[i - 4]
    let t1 = w[i - 3]
    let t2 = w[i - 2]
    let t3 = w[i - 1]
    if (i % 16 === 0) {
      const tmp = t0
      t0 = SBOX[t1] ^ RCON[i / 16 - 1]
      t1 = SBOX[t2]
      t2 = SBOX[t3]
      t3 = SBOX[tmp]
    }
    w[i] = w[i - 16] ^ t0
    w[i + 1] = w[i - 15] ^ t1
    w[i + 2] = w[i - 14] ^ t2
    w[i + 3] = w[i - 13] ^ t3
  }
  return w
}

// AES-128 正向分组加密（16 字节 -> 16 字节）
function aesBlockEncrypt (block, w) {
  const s = new Array(16)
  let i
  let r
  for (i = 0; i < 16; i++) s[i] = block[i] ^ w[i]
  for (r = 1; r <= 10; r++) {
    let t = new Array(16)
    // SubBytes + ShiftRows
    t[0] = SBOX[s[0]]; t[1] = SBOX[s[5]]; t[2] = SBOX[s[10]]; t[3] = SBOX[s[15]]
    t[4] = SBOX[s[4]]; t[5] = SBOX[s[9]]; t[6] = SBOX[s[14]]; t[7] = SBOX[s[3]]
    t[8] = SBOX[s[8]]; t[9] = SBOX[s[13]]; t[10] = SBOX[s[2]]; t[11] = SBOX[s[7]]
    t[12] = SBOX[s[12]]; t[13] = SBOX[s[1]]; t[14] = SBOX[s[6]]; t[15] = SBOX[s[11]]
    // MixColumns（最后一轮跳过）
    if (r !== 10) {
      const m = new Array(16)
      for (let c = 0; c < 4; c++) {
        const j = c * 4
        m[j] = gmul(t[j], 2) ^ gmul(t[j + 1], 3) ^ t[j + 2] ^ t[j + 3]
        m[j + 1] = t[j] ^ gmul(t[j + 1], 2) ^ gmul(t[j + 2], 3) ^ t[j + 3]
        m[j + 2] = t[j] ^ t[j + 1] ^ gmul(t[j + 2], 2) ^ gmul(t[j + 3], 3)
        m[j + 3] = gmul(t[j], 3) ^ t[j + 1] ^ t[j + 2] ^ gmul(t[j + 3], 2)
      }
      t = m
    }
    // AddRoundKey
    for (i = 0; i < 16; i++) s[i] = t[i] ^ w[r * 16 + i]
  }
  return s
}

// ---------------------------------------------------------------------------
// base64 / UTF-8 / 随机数工具
// ---------------------------------------------------------------------------
function bytesToB64 (bytes) {
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

function b64ToBytes (b64) {
  const bin = atob(b64)
  const out = new Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function strToBytes (str) {
  const bin = unescape(encodeURIComponent(str))
  const out = new Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function bytesToStr (bytes) {
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return decodeURIComponent(escape(bin))
}

function randomBytes (len) {
  const out = new Array(len)
  const cryptoObj = typeof window !== 'undefined' ? (window.crypto || null) : null
  if (cryptoObj && cryptoObj.getRandomValues) {
    const buf = new Uint8Array(len)
    cryptoObj.getRandomValues(buf)
    for (let i = 0; i < len; i++) out[i] = buf[i]
  } else {
    for (let j = 0; j < len; j++) out[j] = Math.floor(Math.random() * 256)
  }
  return out
}

// ---------------------------------------------------------------------------
// 运行时密钥：由掩码片段动态还原（源码中不存在完整密钥字符串）
// ---------------------------------------------------------------------------
const _KS = [59, 250, 176, 73, 19, 202, 49, 175, 149, 219, 130, 55, 219, 64, 150, 61]
const _KC0 = 167
const _KC1 = 29

function _getKey () {
  const k = new Array(16)
  for (let i = 0; i < 16; i++) {
    k[i] = (_KS[i] ^ ((_KC0 + i * _KC1) & 0xff)) & 0xff
  }
  return k
}

// ---------------------------------------------------------------------------
// AES-128-CTR 加解密（CTR 模式下加密与解密为同一操作）
// ---------------------------------------------------------------------------
function ctrCrypt (bytes, key, nonce) {
  const w = expandKey(key)
  const out = new Array(bytes.length)
  const counter = new Array(16)
  let oi = 0
  while (oi < bytes.length) {
    for (let n = 0; n < 12; n++) counter[n] = nonce[n]
    const c = Math.floor(oi / 16)
    counter[12] = (c >>> 24) & 0xff
    counter[13] = (c >>> 16) & 0xff
    counter[14] = (c >>> 8) & 0xff
    counter[15] = c & 0xff
    const ks = aesBlockEncrypt(counter, w)
    for (let b = 0; b < 16 && oi < bytes.length; b++, oi++) {
      out[oi] = bytes[oi] ^ ks[b]
    }
  }
  return out
}

// 加密令牌前缀
export const ENC_MARKER = 'ENC$'

/**
 * 对称加密：明文 -> ENC$<随机nonce>$<密文> 令牌（每次结果不同）
 */
export function encryptSecret (plain) {
  const nonce = randomBytes(12)
  const ct = ctrCrypt(strToBytes(String(plain)), _getKey(), nonce)
  return ENC_MARKER + bytesToB64(nonce) + '$' + bytesToB64(ct)
}

/**
 * 对称解密：ENC$ 令牌 -> 明文（解密失败时原样返回，保证业务不被打断）
 */
export function decryptSecret (token) {
  if (typeof token !== 'string' || token.indexOf(ENC_MARKER) !== 0) return token
  try {
    const parts = token.slice(ENC_MARKER.length).split('$')
    if (parts.length !== 2) return token
    const pt = ctrCrypt(b64ToBytes(parts[1]), _getKey(), b64ToBytes(parts[0]))
    return bytesToStr(pt)
  } catch (e) {
    return token
  }
}

export function isEncToken (value) {
  return typeof value === 'string' && value.indexOf(ENC_MARKER) === 0
}

/**
 * 深拷贝任意对象，并把其中所有 ENC$ 令牌还原为明文
 */
export function decryptDeep (obj) {
  if (isEncToken(obj)) return decryptSecret(obj)
  if (Object.prototype.toString.call(obj) === '[object Array]') {
    const arr = []
    for (let i = 0; i < obj.length; i++) arr.push(decryptDeep(obj[i]))
    return arr
  }
  if (obj && typeof obj === 'object') {
    const out = {}
    for (const k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = decryptDeep(obj[k])
    }
    return out
  }
  return obj
}

// ---------------------------------------------------------------------------
// 预置密钥：导出的常量本身就是密文令牌。
// 它们被赋值到工作流字段后，由 axios 发送边界（transformRequest）自动还原明文；
// 内存、console.log、深拷贝中始终保持密文。若确需明文，请显式调用 decryptSecret()。
// ---------------------------------------------------------------------------
// 阿里云 OSS AccessKeyId
export const OSS_ACCESS_KEY_ID = 'ENC$FENOIkRqFWmsll0i$9mbDQ7K1f71ayzwGP1kG2bYwxe+Z2fCT'
// 阿里云 OSS AccessKeySecret
export const OSS_ACCESS_KEY_SECRET = 'ENC$YDPut5vsy1n6M5OF$wizkt8yPLlit6PPzZaqx6cT13roz/lB6F49xR8UU'
// 第三方图像生成服务 API Key（产品精修等）
export const GPT_IMAGE_API_KEY = 'ENC$PCmTK4uhmc8H88Kb$LLqOQPqj/9EZ4mwAWJ+50xNyTnH23SGot5GFQrG4QODxyT4ZmzCzzqZmKBZWyKfIyZqA'
// DashScope/Qwen 大模型 API Key（脚本匹配、视频反推、视频剪辑等）
export const DASHSCOPE_WS_API_KEY = 'ENC$gQ10z3jINOMPQJaG$2bXK35UG+OepcJxIYgoSjOAiu6gnxB3cP+4nla6GTirKMsF0bPeZUdTlwMAj//DCeFZezmgvz83U459agAsLE0j/6R5t2mG/MEu+oxOxs3epYIfEb++ZW21VBmfbkxPoL07ASqPV1V3l4cILEHBB7bUEc3k3'
// 百度翻译 API Key（配套 APIID 20250612002379955）
export const BAIDU_TRANSLATE_API_KEY = 'ENC$CzVLMiS1lbfL1iGz$NxRufRELTMTE50wQ5TaV3rNgt/8='
// 百度翻译 API Key（配套 APIID 20250517002359356）
export const BAIDU_TRANSLATE_API_KEY_2 = 'ENC$3k4D7ZfRs7K9+rS/$Fhjb23KoL8bwdQiaTUaaIq8kIWQ='

// ---------------------------------------------------------------------------
// 发送边界解密：只在请求体真正序列化发往后端的瞬间还原 ENC$ 令牌。
// 内存对象、console.log、JSON.parse(JSON.stringify()) 深拷贝中始终保持密文，
// 避免密钥在控制台/内存中暴露。覆盖 axios（含所有 axios.create 实例）。
// ---------------------------------------------------------------------------

// 递归检测值中是否包含 ENC$ 令牌（快速扫描）
function _containsToken (value) {
  if (typeof value === 'string') return value.indexOf(ENC_MARKER) !== -1
  if (!value || typeof value !== 'object') return false
  if (typeof FormData !== 'undefined' && value instanceof FormData) return false
  if (typeof Blob !== 'undefined' && value instanceof Blob) return false
  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) return false
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      if (_containsToken(value[i])) return true
    }
    return false
  }
  for (const k in value) {
    if (Object.prototype.hasOwnProperty.call(value, k) && _containsToken(value[k])) return true
  }
  return false
}

// 序列化（可能含令牌的）对象为最终请求体字符串
function _serializeBody (data) {
  if (typeof data === 'string') {
    return data.indexOf(ENC_MARKER) !== -1 ? decryptDeepStr(data) : data
  }
  if (data && typeof data === 'object') {
    if (typeof FormData !== 'undefined' && data instanceof FormData) return data
    if (typeof Blob !== 'undefined' && data instanceof Blob) return data
    if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) return data
    if (typeof data.toJSON === 'function') {
      return _serializeBody(data.toJSON())
    }
    if (!_containsToken(data)) return undefined // 无令牌，交给 axios 默认逻辑
    return JSON.stringify(decryptDeep(data))
  }
  return undefined
}

// 对已序列化的 JSON 字符串做令牌还原（先解析再替换，避免误伤普通文本）
function decryptDeepStr (jsonStr) {
  try {
    return JSON.stringify(decryptDeep(JSON.parse(jsonStr)))
  } catch (e) {
    return jsonStr
  }
}

if (axios && axios.defaults && Array.isArray(axios.defaults.transformRequest) && !axios.defaults.transformRequest.__encPatched) {
  const _origTransforms = [].concat(axios.defaults.transformRequest)
  const _encTransform = function encSecretTransform (data, headers) {
    try {
      const body = _serializeBody(data)
      if (body !== undefined) return body
    } catch (e) {
      // 解密异常时回退默认行为，保证业务不被打断
    }
    for (let i = 0; i < _origTransforms.length; i++) {
      const t = _origTransforms[i]
      if (typeof t === 'function') {
        const r = t.call(this, data, headers)
        if (r !== undefined) return r
      }
    }
    return data
  }
  _encTransform.__encPatched = true
  axios.defaults.transformRequest = [_encTransform]
}
